import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { firstName, formatMoney, saleDateText } from './LeadClose.functions';
import type { tCloseFeedRow, tCloseResult } from './LeadClose.types';
import './LeadCloseRecord.styles.scss';

/** "890.00 MXN" as Historial writes amounts, shown as the app does ("$890"); an option label stays as it is. */
function shown(value: string | null): string {
  const match = /^(-?\d+(?:\.\d+)?)\s+[A-Z]{3}$/.exec(value ?? '');
  return match ? formatMoney(match[1]) : (value ?? '');
}

/**
 * Who made this row: the result's author for its creation, or for an edit or
 * deletion they made themselves. An admin acting on someone else's result is
 * not named after the author (empty: the caller says "someone on your team").
 */
export function rowAuthor(row: tCloseFeedRow): string {
  const result = row.result;
  if (!result) return '';
  const byAuthor =
    row.type === 'CREATE' ||
    row.user_uuid === null ||
    row.user_uuid === result.actor_uuid;
  return byAuthor
    ? firstName(result.author_name ?? result.actor_name ?? '')
    : '';
}

type tLeadCloseRecordProps = {
  row: tCloseFeedRow;
  onEdit: (result: tCloseResult) => void;
  onDelete: (result: tCloseResult) => void;
  /** Pencil and trash go on one row per result (its newest); the others only tell. */
  actionable?: boolean;
};

/**
 * A result row of Historial (U15), as the prototype draws it: who did what,
 * the detail, the description, and — when the row says the reader may — a
 * pencil and a trash, always visible and reachable by keyboard: here they are
 * the only way to correct a result.
 */
export default function LeadCloseRecord(
  props: tLeadCloseRecordProps,
): JSX.Element {
  const { row, onEdit, onDelete, actionable = true } = props;
  const { t } = useTranslation('chat/chat');
  const result = row.result;
  const type =
    (row.field.replace('close_', '') as tCloseResult['type']) ?? result?.type;
  const author = rowAuthor(row) || t('leadClose.card.someone');

  let text: string;
  if (row.field === 'close_prior_amount') {
    text = t(
      `leadClose.history.prior.${row.new_value === 'replaced' ? 'replaced' : 'kept'}`,
      { name: author, amount: shown(row.previous_value) },
    );
  } else if (
    row.type === 'DELETE' &&
    result?.removed_reason === 'lead_deleted'
  ) {
    text = t('leadClose.history.removedWithLead');
  } else if (row.type === 'UPDATE') {
    text = t(`leadClose.history.update.${type}`, {
      name: author,
      from: shown(row.previous_value),
      to: shown(row.new_value),
    });
  } else if (row.type === 'DELETE') {
    text = t(`leadClose.history.delete.${type}`, {
      name: author,
      value: shown(row.previous_value),
    });
  } else {
    text = t(`leadClose.history.create.${type}`, {
      name: author,
      amount: shown(row.new_value),
    });
  }

  const reason = result?.option?.label ?? result?.other_text;
  const detail =
    row.field === 'close_prior_amount' || !result
      ? null
      : result.type === 'sold'
        ? result.what_sold
          ? t('leadClose.history.detail.sold', {
              what: result.what_sold,
              date: saleDateText(result.sale_date),
            })
          : t('leadClose.card.saleDate', {
              date: saleDateText(result.sale_date),
            })
        : reason
          ? t(`leadClose.card.reason.${result.type}`, { reason })
          : null;
  const kind = result?.type === 'sold' ? 'sold' : 'result';
  const live =
    Boolean(result?.live) && actionable && row.field !== 'close_prior_amount';

  return (
    <div className="LeadCloseRecord ph-no-capture">
      <div className="LeadCloseRecord__main">
        <span>{text}</span>
        {detail ? <span className="LeadCloseRecord__sub">{detail}</span> : null}
        {live && result?.note ? (
          <span className="LeadCloseRecord__desc">{result.note}</span>
        ) : null}
        <span className="LeadCloseRecord__sub">
          {new Date(`${row.created_at.replace(' ', 'T')}Z`).toLocaleString(
            'es-MX',
            { dateStyle: 'medium', timeStyle: 'short' },
          )}
        </span>
      </div>
      {live && result ? (
        <div className="LeadCloseRecord__actions">
          {result.can_edit ? (
            <button
              type="button"
              className="LeadCloseRecord__action"
              aria-label={t(`leadClose.history.edit.${kind}`)}
              title={t(`leadClose.history.edit.${kind}`)}
              onClick={() => onEdit(result)}
            >
              <PencilSquareIcon aria-hidden />
            </button>
          ) : null}
          {result.can_delete ? (
            <button
              type="button"
              className="LeadCloseRecord__action"
              aria-label={t(`leadClose.history.delete.action.${kind}`)}
              title={t(`leadClose.history.delete.action.${kind}`)}
              onClick={() => onDelete(result)}
            >
              <TrashIcon aria-hidden />
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
